import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

function hashTarget(hash: string): HTMLElement | null {
  if (!hash) return null;
  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return null;
  }
}

/**
 * Makes SPA route changes feel like real page loads.
 *
 * - A new pathname or a new wizard `?step=` (pushed or replaced) jumps to the top of the window, or to the `#hash`
 *   target when one exists in the new page.
 * - Browser Back/Forward (POP) is left alone: <ScrollRestoration /> restores the position the user left at.
 * - The theme sets `scroll-behavior: smooth` on <html>, which would make the new page glide up from wherever the
 *   previous one was scrolled to. The behaviour is neutralised for the duration of the commit so that both this jump
 *   and the router's own restore land instantly. Render this component immediately before <ScrollRestoration /> so
 *   its layout effect runs first.
 */
export function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const step = new URLSearchParams(search).get("step");
  const previous = useRef({ pathname, step });

  useLayoutEffect(() => {
    document.documentElement.style.scrollBehavior = "auto";
    const pageChanged = previous.current.pathname !== pathname || previous.current.step !== step;
    previous.current = { pathname, step };
    if (!pageChanged || navigationType === "POP") return;
    const target = hashTarget(hash);
    if (target) target.scrollIntoView({ behavior: "instant", block: "start" });
    else window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [key, pathname, step, hash, navigationType]);

  // Passive effects run after every layout effect of the same commit, i.e. once <ScrollRestoration /> has scrolled too.
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "";
  }, [key]);

  return null;
}
